import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";

import App from "./App";
import {
  installStaleAssetRecovery,
  isLocalDemoHostname,
  RUNTIME_GENERATION_FILE,
  RUNTIME_GENERATION_META_NAME,
  runtimeGenerationChanged,
} from "./runtime/staleAssetRecovery";
import "./styles/global.css";
import "./styles/docking-theme.css";

// Production uses hashed assets and no-store HTML, but a tab can still request
// a removed lazy chunk after a deploy. Local Vite additionally needs to escape
// Safari's separate back/forward page cache after the demo server restarts.
const localDemoHost = isLocalDemoHostname(window.location.hostname);
const runtimeGenerationMeta = document
  .querySelector<HTMLMetaElement>(`meta[name="${RUNTIME_GENERATION_META_NAME}"]`);
let runtimeGeneration = runtimeGenerationMeta?.content.trim();
const runtimeManifestUrl = new URL(
  `${import.meta.env.BASE_URL}${RUNTIME_GENERATION_FILE}`,
  window.location.origin,
).toString();
if (import.meta.hot && runtimeGenerationMeta) {
  import.meta.hot.on("thread:runtime-generation", (value: unknown) => {
    if (typeof value !== "object" || value === null || Array.isArray(value)) return;
    const generation = (value as { generation?: unknown }).generation;
    if (typeof generation !== "string" || generation.length === 0) return;
    runtimeGeneration = generation;
    runtimeGenerationMeta.content = generation;
  });
}
installStaleAssetRecovery({
  recoverPageCache: import.meta.env.DEV || localDemoHost,
  shouldRecoverPageCache: runtimeGeneration
    ? () => runtimeGeneration
      ? runtimeGenerationChanged(runtimeGeneration, runtimeManifestUrl)
      : false
    : undefined,
});

// Vite sets BASE_URL from the `base` config (e.g. "/thread/" in production, "/" in dev).
// Strip trailing slash for BrowserRouter basename (it expects "/thread" not "/thread/").
const basename = import.meta.env.BASE_URL.replace(/\/$/, "") || "/";

// The Thread CDN can serve the SPA shell at the host root. React Router's
// basename expects the URL to already live under /thread, so move root visitors
// there before mounting the app.
const shouldRedirectHostRoot = basename !== "/" && window.location.pathname === "/";
if (shouldRedirectHostRoot) {
  window.location.replace(`${basename}/${window.location.search}${window.location.hash}`);
}

async function bootstrap() {
  // AG Grid module registration is required since v35. Thread V3 uses paid
  // row grouping/aggregation features, so register Enterprise whenever the
  // package is installed; apply the real license key when the environment has it.
  const agLicenseKey = import.meta.env.VITE_AG_GRID_LICENSE_KEY?.trim();
  ModuleRegistry.registerModules([AllCommunityModule]);

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <BrowserRouter basename={basename}>
        <App />
      </BrowserRouter>
    </StrictMode>,
  );

  // Do not make the whole application wait for the optional Enterprise bundle.
  // On a cold development start this import can take long enough to leave every
  // route — including standalone demos — on a blank screen. Community modules
  // are sufficient to mount the shell; Enterprise augments the registry when it
  // becomes available.
  void import("ag-grid-enterprise")
    .then(({ AllEnterpriseModule, LicenseManager }) => {
      if (agLicenseKey) LicenseManager.setLicenseKey(agLicenseKey);
      ModuleRegistry.registerModules([AllEnterpriseModule]);
    })
    .catch(() => undefined);
}

if (!shouldRedirectHostRoot) {
  void bootstrap();
}
